<template>
  <div id="skuStockStatus" class="pdp-rpa-results mt-1">
    <div v-if="hasRetailers || hasRetailerData" qaid="pdp-rpa-results">
      <div v-if="hasRetailerData" qaid="rpa-disclaimer">
        <section-header size="sm">
          <template #content>
            <div class="sku-stock-status--messaging">
              <p class="sku-stock-status--messaging--disclaimer" qaid="rpa-disclaimer-text">
                <span v-html="bp('stockDisclaimer')"></span>
                <a
                  class="your-location ml-25"
                  qaid="rpa-search-zipcode"
                  @click="rpaModalClick"
                  v-html="retailerData.locationPostalCode ? retailerData.locationPostalCode : bp('yourLocation')"
                />.
              </p>
            </div>
          </template>
        </section-header>
      </div>
      <div v-if="hasRetailers">
        <pdp-rpa-sku-stock-status
          v-for="retailer in retailerData.retailers.slice(0, 3)"
          :key="`retailer-${retailer.id}`"
          class="sku-stock-status--ibd w-full"
          qaid="pdp-rpa-retailer-results"
          :is-bike="isBike"
          :is-modal="false"
          :retailer="retailer"
          :base-properties="baseProperties"
        />
      </div>
      <trek-button secondary block icon="pin_drop" qaid="pdp-rpa-more-retailers-button" @click="rpaModalClick">
        <span v-html="bp('viewMoreRetailers')" />
      </trek-button>
      <el-dialog
        :visible.sync="dialogVisible"
        :modal="true"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
        :z-index="zIndexModal"
        top="0"
        :title="bp('stockStatusHeader')"
        custom-class="rpa-modal__scroll-container"
        qaid="pdp-modal-rpa-results"
        :close-dialog-tooltip="bp('closeDialogToolTip')"
        @close="close"
      >
        <pdp-rpa-modal-search
          v-model="postalCode"
          :class="{'mb-0': !b2b}"
          :retailer-data="retailerData"
          :base-properties="baseProperties"
          qaid="pdp-modal-rpa-search-results"
          @search="performSearch"
        />
        <div v-if="!b2b" class="mb-2">
          <toggle-switch
            id="rpa-instock-toggle-switch"
            name="rpa-instock-toggle-switch"
            validate="required"
            :value="isRpaToggledQaid"
            :qaid="isRpaToggledQaid ? 'pdp-modal-rpa-toggle-switch-on' : 'pdp-modal-rpa-toggle-switch-off'"
            :text-label="bp('stockStatusFilterInStock')"
            @change="rpaModalToggleSwitch"
          />
        </div>
        <div
          class="w-full overflow-y-scroll rpa-modal__sku-status-container mb-1"
          :class="{iphonex: isDetectiPhoneX, iphone: isIphoneAndSafari}"
        >
          <div v-if="isLocationPostalCodeCorrect">
            <pdp-rpa-sku-stock-status
              v-for="retailer in retailerData.retailers"
              :key="`retailer-${retailer.id}`"
              class="sku-modal-status--ibd w-full"
              :is-bike="isBike"
              :retailer="retailer"
              :is-modal="isModal"
              :base-properties="baseProperties"
            />
          </div>
          <div v-else>
            <div class="w-full text-center" qaid="pdp-modal-rpa-search-empty-results">
              <span v-html="bp('retailerSearchEmpty')" /><br />
              <span v-html="bp('retailerSearchNoResults')" />
            </div>
          </div>
          <div v-if="isLocationPostalCodeCorrect" class="w-full text-center">
            <p
              class="text-xs italic text-gray-60"
              qaid="rpa-modal-disclaimer-text"
              v-html="bp('stockStatusUpdateDisclaimer')"
            />
          </div>
        </div>
      </el-dialog>
    </div>
    <div v-else>
      <div class="cart-find-retailer">
        <template v-if="!hasProduct || (!hasRetailerData && !product.retailerOnly && options.eCommAvailable)">
          <pdl-link
            id="find-trek-retailer-store-cta"
            button
            secondary
            block
            icon="pin_drop"
            :href="withContextPath('/store-finder/')"
            qaid="pdp-rpa-find-retailer-store"
          >
            <span v-html="bp('retailerButtonCaption')" />
          </pdl-link>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import isArray from 'lodash/isArray';
import {Url} from '@/utils/url';
import {mapGetters, mapState} from 'vuex';
import PdpRpaSkuStockStatus from '@/components/containers/pdp/PdpRpaSkuStockStatus';
import ElDialog from '@/components/Dialog';
import PdpRpaModalSearch from '@/components/containers/pdp/PdpRpaModalSearch';
import {Cookie} from '@/utils/cookie';
import TrekButton from '@/components/TrekButton';
import {PdlLink} from '@pedal/pdl-link';
import ToggleSwitch from '@/components/ToggleSwitch.vue';
import SectionHeader from '@/components/SectionHeader';
import {retailerActions} from '@/store/modules/retailer-search';
import Variant from '@/utils/variant-helpers';

export default {
  name: 'PdpRpaResults',
  components: {
    PdpRpaSkuStockStatus,
    ElDialog,
    PdpRpaModalSearch,
    PdlLink,
    TrekButton,
    ToggleSwitch,
    SectionHeader,
  },
  props: {
    isBike: {
      type: Boolean,
      required: true,
    },
    retailerData: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    isLocationPostalCodeCorrect: {
      type: Boolean,
      required: true,
    },
    product: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    options: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      dialogVisible: false,
      isRpaToggledQaid: this.rpaViewInStock,
      zIndexModal: 16000011,
      isModal: false,
      postalCode: this.retailerData.locationPostalCode || Cookie.get('trek_rpa_user_search_phrase') || '',
    };
  },
  computed: {
    ...mapGetters('backend', ['b2b']),
    ...mapGetters('pdp', ['rpaViewInStock']),
    ...mapState('pdp', ['rpaViewInStock']),
    hasRetailers() {
      const retailers = this.retailerData?.retailers;
      return isArray(retailers) && retailers.length > 0;
    },
    hasRetailerData() {
      return this.retailerData && Object.keys(this.retailerData).length > 0;
    },
    hasProduct() {
      return this.product && Object.keys(this.product).length > 0;
    },
    isDetectiPhoneX() {
      const ratio = window.devicePixelRatio || 1;
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      let currentScreen = {
        width: window.screen.width * ratio,
        height: window.screen.height * ratio,
      };
      if (iOS && currentScreen.width === 1125 && currentScreen.height === 2436) {
        return true;
      } else {
        return false;
      }
    },
    isIphoneAndSafari() {
      if (
        navigator.platform.indexOf('iPhone') != -1 &&
        navigator.userAgent.indexOf('Safari') != -1 &&
        navigator.userAgent.indexOf('Chrome') == -1
      ) {
        return true;
      } else {
        return false;
      }
    },
  },
  mounted() {
    this.isRpaToggledQaid = this.rpaViewInStock;
  },
  methods: {
    performSearch(locationPostalCode) {
      const variant = Variant(this.retailerData.sku, this.product, this.b2b).get();
      const payload = {
        variant,
        inStockOnly: this.isRpaToggledQaid,
        locationPostalCode: locationPostalCode || this.postalCode,
      };
      this.$store.dispatch(retailerActions.UPDATE_RETAILER_AVAILABILITY, payload);
    },
    withContextPath: (url) => Url.withContextPath(url),
    rpaModalClick() {
      if (this.isRpaToggledQaid) {
        this.performSearch();
      }
      this.isModal = true;
      this.dialogVisible = true;
      if (this.hasRetailers && this.isLocationPostalCodeCorrect) {
        this.postalCode = this.retailerData.locationPostalCode;
      }
    },
    close() {
      this.dialogVisible = false;
      this.isModal = false;
    },

    rpaModalToggleSwitch() {
      this.performSearch();
      if (this.isRpaToggledQaid === true) {
        this.isRpaToggledQaid = false;
      } else {
        this.isRpaToggledQaid = true;
      }
    },
  },
};
</script>
